import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import { InfoCircleFilled } from '@ant-design/icons';
import {
  Spin,
  Alert,
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
} from 'antd';
import { canVisitAdminSpace, isSourcer, isSupplier } from 'utils/rbac';

const DIVISIONS = [
  'Southwest',
  'NorCal',
  'Seattle',
  'Intermountain',
  'Portland',
  'Denver',
  'Southern',
  'Mid-Atlantic',
  'Shaws',
  'Jewel',
  'SoCal',
];

const SsoCallback: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApplyAccess, setShowApplyAccess] = useState(false);
  const [userClaims, setUserClaims] =
    useState<{ name: string; email: string } | null>(null);
  const [applyingAccess, setApplyingAccess] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const handleSsoCallback = async () => {
      try {
        // Get authorization code from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');

        if (errorParam) {
          setError('SSO authentication was cancelled or failed.');
          setLoading(false);
          return;
        }

        if (!code) {
          setError('Authorization code not found in callback URL.');
          setLoading(false);
          return;
        }

        if (!state) {
          setError('State parameter not found in callback URL.');
          setLoading(false);
          return;
        }

        // Exchange code for JWT token
        const result = await dispatch.app.loginWithSso({ code, state });
        setLoading(false);

        if (result.user) {
          // Successful login
          // Redirect to appropriate page based on user role
          if (isSupplier(result.user) || isSourcer(result.user)) {
            window.location.pathname = '/workspace';
          } else if (canVisitAdminSpace(result.user)) {
            window.location.pathname = '/platform/org';
          } else {
            window.location.pathname = '/';
          }
        } else if (result.response.errorType === 'NO_ACCOUNT') {
          // No account found, show apply for access
          setShowApplyAccess(true);
          setUserClaims(result.response.claims);
        } else {
          // Other errors
          setError(
            result.response.errorType ||
              'Unknown error occurred during SSO authentication.'
          );
        }
      } catch (err: any) {
        console.error('SSO callback error:', err);
        setError(err.message || 'An error occurred during SSO authentication.');
        setLoading(false);
      }
    };

    handleSsoCallback();
  }, [location.search, dispatch.app, history]);

  const handleRegister = async () => {
    try {
      const values = await form.validateFields();
      if (!userClaims?.email || !userClaims?.name) return;

      setApplyingAccess(true);
      const result = await dispatch.app.selfRegister({
        email: userClaims.email,
        name: userClaims.name,
        division: values.division,
        code: new URLSearchParams(location.search).get('code'),
      });

      if (result.user) {
        message.success('Registration successful. You are now logged in.');
        setIsModalVisible(false);

        // Redirect based on user role
        if (isSupplier(result.user) || isSourcer(result.user)) {
          window.location.pathname = '/workspace';
        } else if (canVisitAdminSpace(result.user)) {
          window.location.pathname = '/platform/org';
        } else {
          window.location.pathname = '/';
        }
      } else {
        // Fallback for cases where registration might not return a user immediately or failed without throw
        message.info(
          'Registration submitted. Please check if you can login or wait for approval.'
        );
        setIsModalVisible(false);
        setTimeout(() => {
          window.location.pathname = '/login';
        }, 2000);
      }
    } catch (err: any) {
      console.error('Self registration error:', err);
      // Only show error if it's not a form validation error
      if (!err.errorFields) {
        const errorMessage =
          err.message || 'Failed to register. Please try again.';
        message.error(errorMessage);
      }
    } finally {
      setApplyingAccess(false);
    }
  };

  const handleReturnToLogin = () => {
    window.location.pathname = '/login';
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '40px 20px',
        }}
      >
        <Spin size="large" />
        <p style={{ marginTop: '16px', textAlign: 'center' }}>
          Completing SSO authentication...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          maxWidth: '400px',
          margin: '40px auto',
          padding: '20px',
        }}
      >
        <Alert
          message="SSO Authentication Error"
          description={error}
          type="error"
          showIcon
          action={
            <button
              onClick={handleReturnToLogin}
              style={{
                border: 'none',
                background: 'none',
                color: '#1890ff',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Return to Login
            </button>
          }
        />
      </div>
    );
  }

  if (showApplyAccess && userClaims) {
    return (
      <div
        style={{
          maxWidth: '600px',
          margin: '40px auto',
          padding: '20px',
        }}
      >
        <Alert
          message={`Email Not Registered`}
          description={
            <div>
              <p>Your Email {userClaims.email} is not registered.</p>
              <p>Do you want to self-register for access?</p>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '20px' }}
        />
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Button
            type="primary"
            onClick={() => {
              form.setFieldsValue({
                name: userClaims.name,
                email: userClaims.email,
              });
              setIsModalVisible(true);
            }}
          >
            Yes, Register
          </Button>
          <Button onClick={handleReturnToLogin}>No, Return to Login</Button>
        </div>

        <Modal
          title="Register for Access"
          visible={isModalVisible}
          onOk={handleRegister}
          onCancel={() => setIsModalVisible(false)}
          confirmLoading={applyingAccess}
          okText="Register"
        >
          <Form form={form} layout="vertical">
            <Form.Item label="Name" name="name">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Email" name="email">
              <Input disabled />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Division{' '}
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'nowrap' }}>
                        If you are a corporate or remote employee, please select
                        the division location you are closest to.
                      </span>
                    }
                    overlayStyle={{ maxWidth: 'none' }}
                  >
                    <InfoCircleFilled style={{
                                        fontSize: '14px',
                                        color: "#1890ff",
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                      }}></InfoCircleFilled>
                  </Tooltip>
                </span>
              }
              name="division"
              rules={[
                { required: true, message: 'Please select your division' },
              ]}
            >
              <Select placeholder="Select a division">
                {DIVISIONS.map((div) => (
                  <Select.Option key={div} value={div}>
                    {div}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  return null;
};

export default SsoCallback;
